import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "481",
  "slug": "largest-bays-and-gulfs-in-the-world-ranked",
  "title": "The Largest Bays and Gulfs in the World, Ranked",
  "excerpt": "From the vast Bay of Bengal to the Gulf of Mexico, here are the biggest bays and gulfs on Earth ranked by area, plus what separates a bay from a gulf in the first place.",
  "date": "2026-06-18",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1508971607899-a238a095d417?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxvY2VhbiUyMGJheSUyMGNvYXN0bGluZSUyMGFlcmlhbHxlbnwxfDB8fHwxNzgxNzcwNDI5fDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Where the ocean takes a big bite out of the land, you get a bay or a gulf, a sheltered arm of sea that has shaped trade routes, fisheries, and entire civilisations. The largest of them are genuinely enormous, big enough to hold whole seas of their own. Here are the biggest, ranked roughly by area, along with a note on why measuring them is trickier than it sounds."
    },
    {
      "type": "heading",
      "text": "Bay vs Gulf: What Is the Difference?"
    },
    {
      "type": "paragraph",
      "text": "People often assume a gulf is just a large bay, and that is roughly true, but the terms are not used consistently. Both describe a body of water partly enclosed by land. In general usage, a gulf tends to be deeper and more enclosed, often connected to the open ocean by a relatively narrow mouth or strait, while a bay is broader and more open. But the naming is mostly historical accident, not strict geography. The Bay of Bengal is far larger than the Gulf of Mexico, despite the names suggesting otherwise. Because the boundaries of these features are defined differently from one source to another, the areas below should be read as approximate."
    },
    {
      "type": "heading",
      "text": "The Largest by Area"
    },
    {
      "type": "paragraph",
      "text": "Ranked from largest, the giants of the world's coastlines look something like this:"
    },
    {
      "type": "list",
      "items": [
        "Bay of Bengal, around 2.2 million square kilometres. The largest bay on Earth by a wide margin, bordered by India, Bangladesh, Myanmar, and Sri Lanka, and fed by great rivers like the Ganges.",
        "Gulf of Mexico, around 1.5 million square kilometres. Almost enclosed by the United States, Mexico, and Cuba, and a major source of oil, fisheries, and hurricanes.",
        "Gulf of Guinea, on the order of a million square kilometres or more depending on where you draw its edge. The great curve of the West African coast, sitting near the intersection of the equator and the prime meridian.",
        "Hudson Bay, around 1.2 million square kilometres. A vast, shallow, ice-covered inland sea in northern Canada that freezes over for much of the year.",
        "Gulf of Alaska, a broad, stormy arm of the North Pacific framed by the mountains and glaciers of southern Alaska."
      ]
    },
    {
      "type": "paragraph",
      "text": "Beyond the very largest, other major bays and gulfs shape their regions profoundly: the Bay of Biscay off France and Spain, the Persian Gulf with its oil wealth, the Gulf of Carpentaria in northern Australia, the Great Australian Bight, and the Gulf of Saint Lawrence in Canada."
    },
    {
      "type": "heading",
      "text": "How a Bay Forms"
    },
    {
      "type": "paragraph",
      "text": "Bays and gulfs are carved by a mix of forces working over very different timescales. Some open up where the crust itself pulls apart, the Persian Gulf and the Red Sea sit in zones where plates are rifting. Others are drowned river valleys and lowlands flooded when sea levels rose after the last ice age, which is why so many coastlines have a soft, scalloped edge. Softer rock erodes faster than the headlands around it, so the sea eats inward to form a curve, while glaciers gouged out many of the deep, steep-sided bays of the far north. Over thousands of years, water finds and widens every weakness in a coast."
    },
    {
      "type": "heading",
      "text": "Why These Features Matter"
    },
    {
      "type": "paragraph",
      "text": "Bays and gulfs are not just scenery. Their sheltered waters make natural harbours, which is why so many great port cities sit on them. Their shape concentrates tides and storms, the Bay of Fundy has the highest tides on Earth, and the Gulf of Mexico funnels hurricanes toward the coast. They support some of the world's richest fishing grounds, and their seabeds hold much of the planet's offshore oil and gas. A gulf can define a region's economy, climate, and history all at once."
    },
    {
      "type": "heading",
      "text": "How to Recognise Them From Above"
    },
    {
      "type": "paragraph",
      "text": "From a satellite view, big bays and gulfs are some of the easiest features to read, and they are powerful location clues. The deep, near-circular bite of the Gulf of Mexico, the broad sweep of the Bay of Bengal with its sediment-stained river mouths, the cold, island-fringed expanse of Hudson Bay: each has a shape you can learn to recognise instantly. Coastlines are often the fastest way to place yourself on the globe."
    },
    {
      "type": "heading",
      "text": "Test Your Coastline Knowledge"
    },
    {
      "type": "paragraph",
      "text": "Knowing the world's great bays and gulfs by their outlines is a real geography superpower, and it is exactly the kind of knowledge that pays off in a location-guessing game. EarthGuessr drops you into satellite imagery from around the world and challenges you to work out where you are. Recognise the curve of a famous gulf and you are already halfway to the right answer."
    }
  ]
};
